import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Camera, Film, Map as MapIcon } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PHOTO_DURATION_MS = 6500;
const MAP_DURATION_MS = 5000;

// Defensiv detektion: media_typ='video' ELLER bild_url-ändelse är en videofiltyp.
const VIDEO_EXTS_RE = /\.(mp4|mov|webm|m4v|m4a|ogg|ogv)(\?|$)/i;
const isVideoMedia = (b) =>
  ((b?.media_typ || "").toLowerCase() === "video") || VIDEO_EXTS_RE.test(b?.bild_url || "");
const isMapSlide = (b) => b?.kind === "map";

/**
 * Auto-advancing slideshow banner for Home.
 * Photos: fixed 6.5s display
 * Videos: advance after video ends (or when duration reached)
 * Map slide: rendered if `mapSlide` prop given. Shown first, 8s display.
 *
 * Props:
 *   items     — array of {id, titel, fotograf, ort, bild_url, media_typ, varaktighet_sek}
 *   mapSlide  — optional {markers, lines, center, zoom, title, subtitle}
 *               When provided, prepended as first slide.
 */
export default function MediaSlideshow({ items = [], mapSlide = null }) {
  // Prepend map slide as a synthetic item that knows how to render itself
  const composedItems = mapSlide
    ? [{ id: "__map__", kind: "map", ...mapSlide }, ...items]
    : items;
  const [idx, setIdx] = useState(0);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const advance = useCallback(() => {
    if (composedItems.length <= 1) return;
    setIdx((i) => (i + 1) % composedItems.length);
  }, [composedItems.length]);

  const goBack = () => {
    clearTimer();
    setIdx((i) => (i - 1 + composedItems.length) % composedItems.length);
  };

  const goForward = () => {
    clearTimer();
    advance();
  };

  // Schedule transition when item changes
  useEffect(() => {
    clearTimer();
    if (composedItems.length === 0) return;
    const current = composedItems[idx];
    if (!current) return;
    if (isMapSlide(current)) {
      timerRef.current = setTimeout(advance, MAP_DURATION_MS);
    } else if (isVideoMedia(current)) {
      const cap = Math.min((current.varaktighet_sek || 90) + 1, 95) * 1000;
      timerRef.current = setTimeout(advance, cap);
    } else {
      timerRef.current = setTimeout(advance, PHOTO_DURATION_MS);
    }
    return clearTimer;
  }, [idx, composedItems, advance]);

  if (composedItems.length === 0) {
    return (
      <div data-testid="slideshow-empty" className="w-full h-full flex items-center justify-center bg-ink">
        <div className="text-center text-parchment/60 px-6">
          <Camera size={40} className="mx-auto mb-3 opacity-40" />
          <p className="font-heading text-xl text-parchment/80">Inga bidrag ännu</p>
          <p className="text-sm mt-1">Bli först &ndash; <Link to="/bilder/skicka" className="text-parchment underline hover:text-parchment/80">skicka in en bild eller video</Link></p>
        </div>
      </div>
    );
  }

  const current = composedItems[idx];
  const url = current && !isMapSlide(current) ? (current.bild_url.startsWith("http") ? current.bild_url : `${BACKEND}${current.bild_url}`) : "";

  return (
    <div data-testid="slideshow" className="vidalv-slideshow relative w-full h-full bg-ink overflow-hidden">
      {/* Media frames stacked; only current visible */}
      {composedItems.map((item, i) => {
        if (isMapSlide(item)) {
          return (
            <div
              key={item.id}
              data-testid={`slideshow-slide-${i}`}
              className="absolute inset-0 transition-opacity duration-700"
              style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? "auto" : "none" }}
            >
              {/* lazy-import for first-paint perf */}
              <SlideshowMap markers={item.markers} lines={item.lines} center={item.center} zoom={item.zoom} active={i === idx} />
              {/* Bottom gradient for caption */}
              <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(44,39,36,0.85), rgba(44,39,36,0))" }} />
            </div>
          );
        }
        const itemUrl = item.bild_url.startsWith("http") ? item.bild_url : `${BACKEND}${item.bild_url}`;
        return (
          <div
            key={item.id}
            data-testid={`slideshow-slide-${i}`}
            className="absolute inset-0 transition-opacity duration-700"
            style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? "auto" : "none" }}
          >
            {isVideoMedia(item) ? (
              i === idx ? (
                <video
                  ref={videoRef}
                  src={itemUrl}
                  autoPlay
                  muted
                  playsInline
                  onEnded={advance}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : null
            ) : (
              <img
                src={itemUrl}
                alt={item.titel || ""}
                className="absolute inset-0 w-full h-full object-cover"
                loading={i === 0 ? "eager" : "lazy"}
              />
            )}
            {/* Bottom gradient for caption */}
            <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(44,39,36,0.85), rgba(44,39,36,0))" }} />
          </div>
        );
      })}

      {/* Caption (uses current slide) */}
      {current && (
        <div className="absolute inset-x-0 bottom-0 px-4 sm:px-8 pb-6 pt-10 pointer-events-none">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 mb-1">
              {isMapSlide(current) ? <MapIcon size={12} className="text-parchment/70" /> :
                isVideoMedia(current) ? <Film size={12} className="text-parchment/70" /> :
                <Camera size={12} className="text-parchment/70" />}
              <p className="font-body text-[10px] text-parchment/60 uppercase tracking-[0.2em]">
                {isMapSlide(current) ? "vidalv på kartan" : "Ur bildsamlingen"}
              </p>
            </div>
            <h2 className="font-heading text-xl sm:text-2xl lg:text-3xl font-semibold text-parchment leading-tight" data-testid="slideshow-title">
              {current.titel || (isMapSlide(current) ? "vidalv – platser i bygden" : "")}
            </h2>
            {!isMapSlide(current) && current.fotograf && (
              <p className="text-xs sm:text-sm text-parchment/70 mt-1">
                Av {current.fotograf}{current.ort && <span className="text-parchment/50"> &middot; {current.ort}</span>}
              </p>
            )}
            {isMapSlide(current) && current.subtitle && (
              <p className="text-xs sm:text-sm text-parchment/70 mt-1 pointer-events-auto">
                {current.subtitle} <Link to="/karta" className="text-parchment underline hover:text-parchment/80 ml-1">Öppna kartan</Link>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Nav arrows (only if multiple) */}
      {composedItems.length > 1 && (
        <>
          <button
            data-testid="slideshow-prev"
            onClick={goBack}
            aria-label="Föregående"
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-ink/40 hover:bg-ink/70 text-parchment rounded-full transition-colors backdrop-blur-sm"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            data-testid="slideshow-next"
            onClick={goForward}
            aria-label="Nästa"
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-ink/40 hover:bg-ink/70 text-parchment rounded-full transition-colors backdrop-blur-sm"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Dots indicator */}
      {composedItems.length > 1 && composedItems.length <= 12 && (
        <div className="absolute top-4 right-4 flex gap-1.5">
          {composedItems.map((_, i) => (
            <button
              key={i}
              data-testid={`slideshow-dot-${i}`}
              onClick={() => { clearTimer(); setIdx(i); }}
              aria-label={`Till bild ${i + 1}`}
              className={`w-2 h-2 rounded-full transition-all ${i === idx ? "bg-parchment w-6" : "bg-parchment/40 hover:bg-parchment/70"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Lightweight Leaflet renderer för slideshow-kartan. Lazy-importeras endast
// när map-sliden faktiskt visas så att första bildvisningen inte påverkas.
function SlideshowMap({ markers = [], lines = [], center, zoom, active }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [tilesReady, setTilesReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!containerRef.current || mapRef.current) return;
    (async () => {
      const L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");
      if (cancelled || !containerRef.current) return;
      const map = L.map(containerRef.current, {
        center: center ? [center[1], center[0]] : [63.870, 16.380],
        zoom: zoom || 10,
        zoomControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        touchZoom: false,
        keyboard: false,
        attributionControl: true,
      });
      mapRef.current = map;
      // OSM som bas: pålitligt världstäckande, har Sverige med vägar/sjöar.
      const tile = L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: '© OpenStreetMap',
      });
      tile.on("load", () => { if (!cancelled) setTilesReady(true); });
      tile.addTo(map);

      lines.forEach((l) => {
        if (!l.coords || l.coords.length < 2) return;
        const latlngs = l.coords.map(([x, y]) => [y, x]);
        L.polyline(latlngs, { color: "#f4ead4", weight: 6, opacity: 0.7 }).addTo(map);
        L.polyline(latlngs, { color: l.color || "#3F6B4E", weight: 2.5, opacity: 0.95, dashArray: "6 6" }).addTo(map);
      });

      markers.forEach((m) => {
        const dot = L.divIcon({
          html: `<span style="display:block;width:10px;height:10px;border-radius:50%;background:#B05B41;box-shadow:0 0 0 2.5px #FDFBF7,0 0 0 3.5px #B05B41"></span>`,
          className: "vidalv-leaflet-marker",
          iconSize: [10, 10], iconAnchor: [5, 5],
        });
        L.marker([m.lat, m.lng], { icon: dot, interactive: false, keyboard: false }).addTo(map);
      });

      const ro = new ResizeObserver(() => {
        if (mapRef.current && containerRef.current) {
          try { mapRef.current.invalidateSize(); } catch { /* race med unmount */ }
        }
      });
      ro.observe(containerRef.current);
      map.invalidateSize();
      // Fallback: även om "load"-eventet aldrig kommer, markera tiles som klara efter 1.5s
      // så att kartan visas mjukt även på långsamma anslutningar.
      setTimeout(() => { if (!cancelled) setTilesReady(true); }, 1500);
    })();
    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (active && mapRef.current) {
      setTimeout(() => {
        if (mapRef.current) {
          try { mapRef.current.invalidateSize(); } catch { /* race med unmount */ }
        }
      }, 80);
    }
  }, [active]);

  return (
    <div className="absolute inset-0 w-full h-full bg-parchment overflow-hidden" data-testid="slideshow-map-canvas">
      <div ref={containerRef} className="absolute inset-0 w-full h-full transition-opacity duration-500" style={{ opacity: tilesReady ? 1 : 0 }} />
      {!tilesReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-bone-alt">
          <div className="text-bark/60 text-sm">Läser in kartan…</div>
        </div>
      )}
    </div>
  );
}
